import { RiaSourceTriggerLocation } from '@/analytics/enums/ria.enums'
import { CitatorSurface } from '@/analytics/enums/citator.enums'
import { kUnverifiedCitationLabel, kUnverifiedSourceLabel } from '@/agent/references/constants'
import { useCitationSourceTooltip } from '@/agent/references/hooks/useCitationSourceTooltip'
import CitationSourceTooltip from '@/agent/references/components/CitationSourceTooltip'

type AgentResponseCitationBadgeProps = {
  citation_id: string
  citation_id_index: number
  citationLabel: string | undefined
}

/**
 * Numbered citation badge ([1], [1.2]) in a chat response. Hover (~1s) shows the source
 * tooltip; clicking opens the content viewer at this occurrence's highlights.
 *
 * When the citation id can't be mapped to a reference — mid-stream before the payload
 * arrives, or an id the turn doesn't know — the badge renders visibly distinct
 * (grey [?], "Not linked" tooltip) and non-clickable. (The canvas shows a greyed-out
 * fallback number instead: references don't persist across artifact versions.)
 */
export default function AgentResponseCitationBadge(props: AgentResponseCitationBadgeProps) {
  const { citation_id, citation_id_index, citationLabel } = props

  const { reference, isActive, title, caseStatus, isOpening, error, handleTooltipOpen, openCitation, handleViewCitationAnalysis } = useCitationSourceTooltip({
    citation_id,
    citation_id_index,
    triggerLocation: RiaSourceTriggerLocation.CitationBadge,
    surface: CitatorSurface.Chat,
  })

  if (!reference) {
    return (
      <span title={kUnverifiedSourceLabel} className="text-brand-neutral-400 px-1" aria-label={kUnverifiedSourceLabel}>
        [{kUnverifiedCitationLabel}]
      </span>
    )
  }

  // Hide the badge if the display label is not found.
  if (!citationLabel) {
    return null
  }

  return (
    <CitationSourceTooltip
      reference={reference}
      citationLabel={citationLabel}
      caseStatus={caseStatus}
      isOpening={isOpening}
      error={error}
      onOpen={handleTooltipOpen}
      onViewReference={openCitation}
      onViewCitationAnalysis={() => void handleViewCitationAnalysis()}
    >
      <button
        onClick={openCitation}
        className={`text-brand-700 px-1 hover:text-brand-800 ${isActive ? ' bg-brand-neutral-200 rounded-md' : ''}`}
        aria-label={`Citation ${citationLabel}: ${title}`}
      >
        [{citationLabel}]
      </button>
    </CitationSourceTooltip>
  )
}
